import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Bell, BellOff, BellRing, Heart, Pill, Calendar,
  AlertTriangle, MessageCircle, ClipboardList, Check,
  Loader2, Settings, Moon, Smartphone, Trash2, Clock
} from "lucide-react";

const ICON_MAP = {
  heart: Heart,
  pill: Pill,
  calendar: Calendar,
  "alert-triangle": AlertTriangle,
  "message-circle": MessageCircle,
  "clipboard-list": ClipboardList
};

/**
 * Push Notifications Manager
 * Subscribe, configure preferences, and view notification history
 */
export default function PushNotificationManager() {
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [types, setTypes] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [subscriptions, setSubscriptions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, prefsRes, subsRes, historyRes] = await Promise.all([
        api.get("/push-notifications/types"),
        api.get("/push-notifications/preferences").catch(() => ({ data: { preferences: {} } })),
        api.get("/push-notifications/subscriptions").catch(() => ({ data: { subscriptions: [] } })),
        api.get("/push-notifications/history?limit=10").catch(() => ({ data: { notifications: [] } }))
      ]);

      setTypes(typesRes.data.types || []);
      setPreferences(prefsRes.data.preferences || {});
      setSubscriptions(subsRes.data.subscriptions || []);
      setNotifications(historyRes.data.notifications || []);
      setIsSubscribed(subsRes.data.subscriptions?.length > 0);
    } catch (error) {
      console.error("Error loading notification data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const subscribeToPush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      toast.error("Push notifications are not supported in this browser");
      return;
    }

    setSubscribing(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast.error("Notification permission denied");
        return;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          process.env.REACT_APP_VAPID_PUBLIC_KEY || 
          "BNxW3p7wWZyGOoHp1AvEK1YW1gJdBZdZLmBDvLhqUPLqJBr7c0_mJ3LQUKSmFqVE1xPzpQl4eGYR0_KJNFuKUqk"
        )
      });

      // Send to server
      const response = await api.post("/push-notifications/subscribe", {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey("p256dh")))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey("auth"))))
        },
        device_name: navigator.userAgent.includes("Mobile") ? "Mobile Device" : "Desktop"
      });

      if (response.data.success) {
        toast.success("Push notifications enabled!");
        setIsSubscribed(true);
        loadData();
      }
    } catch (error) {
      console.error("Push subscription error:", error);
      toast.error("Failed to enable push notifications");
    } finally {
      setSubscribing(false);
    }
  };

  const unsubscribeFromPush = async (subscriptionId) => {
    try {
      await api.delete(`/push-notifications/subscribe/${subscriptionId}`);
      toast.success("Push notifications disabled");
      loadData();
    } catch (error) {
      toast.error("Failed to disable push notifications");
    }
  };

  const updatePreference = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    
    try {
      await api.put("/push-notifications/preferences", newPrefs);
    } catch (error) {
      toast.error("Failed to update preference");
      setPreferences(preferences);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await api.put("/push-notifications/preferences", preferences);
      toast.success("Preferences saved");
      setShowSettings(false);
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/push-notifications/read/${notificationId}`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error("Error marking notification as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put("/push-notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A7C6F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="push-notification-manager">
      {/* Header Card */}
      <Card className="border-0 shadow-md bg-gradient-to-r from-[#4A7C6F] to-[#6B9B8A] text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                {isSubscribed ? (
                  <BellRing className="w-7 h-7" />
                ) : (
                  <BellOff className="w-7 h-7" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold">Push Notifications</h2>
                <p className="text-white/80">
                  {isSubscribed 
                    ? "You&apos;re receiving real-time alerts"
                    : "Enable notifications to stay updated"
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isSubscribed && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowSettings(true)}
                  className="text-white hover:bg-white/20"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              )}
              <Button
                onClick={isSubscribed ? () => unsubscribeFromPush(subscriptions[0]?.id) : subscribeToPush}
                disabled={subscribing}
                className={isSubscribed 
                  ? "bg-white/20 hover:bg-white/30 text-white"
                  : "bg-white text-[#4A7C6F] hover:bg-white/90"
                }
              >
                {subscribing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : isSubscribed ? (
                  <BellOff className="w-4 h-4 mr-2" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                {isSubscribed ? "Disable" : "Enable Notifications"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Preferences */}
      {isSubscribed && (
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Notification Types</CardTitle>
            <CardDescription>Choose what you want to be notified about</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {types.map(type => {
                const Icon = ICON_MAP[type.icon] || Bell;
                const isEnabled = preferences[type.id] !== false;
                
                return (
                  <div 
                    key={type.id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isEnabled 
                        ? "border-[#4A7C6F] bg-[#E8F4F0]" 
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isEnabled ? "bg-[#4A7C6F] text-white" : "bg-slate-200 text-slate-500"
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(v) => updatePreference(type.id, v)}
                      />
                    </div>
                    <h4 className="font-medium text-slate-800">{type.name}</h4>
                    <p className="text-xs text-slate-500 mt-1">{type.description}</p>
                    <Badge 
                      variant="outline" 
                      className={`mt-2 text-xs ${
                        type.priority === "high" 
                          ? "border-red-200 text-red-600" 
                          : "border-slate-200 text-slate-500"
                      }`}
                    >
                      {type.priority} priority
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Notifications */}
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Notifications</CardTitle>
              <CardDescription>Your notification history</CardDescription>
            </div>
            {notifications.some(n => !n.read) && (
              <Button variant="outline" size="sm" onClick={markAllAsRead}>
                <Check className="w-4 h-4 mr-2" />
                Mark all read
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No notifications yet</p>
              <p className="text-sm">You&apos;ll see alerts here when they arrive</p>
            </div>
          ) : (
            <ScrollArea className="h-[300px]">
              <div className="space-y-3 pr-4">
                {notifications.map(notification => {
                  const Icon = ICON_MAP[notification.icon] || Bell;
                  
                  return (
                    <div 
                      key={notification.id}
                      className={`p-4 rounded-lg border transition-all cursor-pointer hover:bg-slate-50 ${
                        notification.read ? "bg-white border-slate-200" : "bg-blue-50 border-blue-200"
                      }`}
                      onClick={() => !notification.read && markAsRead(notification.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          notification.severity === "critical" 
                            ? "bg-red-100 text-red-600"
                            : "bg-[#E8F4F0] text-[#4A7C6F]"
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-slate-800">{notification.title}</h4>
                            <span className="text-xs text-slate-400">
                              {formatTime(notification.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{notification.body}</p>
                          {!notification.read && (
                            <Badge className="mt-2 bg-blue-100 text-blue-700">New</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#4A7C6F]" />
              Notification Settings
            </DialogTitle>
            <DialogDescription>
              Customize when and how you receive notifications
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Quiet Hours */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Moon className="w-5 h-5 text-slate-500" />
                  <div>
                    <Label className="font-medium">Quiet Hours</Label>
                    <p className="text-xs text-slate-500">Pause non-urgent notifications</p>
                  </div>
                </div>
                <Switch
                  checked={preferences.quiet_hours_enabled || false}
                  onCheckedChange={(v) => setPreferences(prev => ({ ...prev, quiet_hours_enabled: v }))}
                />
              </div>
              
              {preferences.quiet_hours_enabled && (
                <div className="grid grid-cols-2 gap-4 pl-8">
                  <div className="space-y-2">
                    <Label className="text-xs">Start Time</Label>
                    <input
                      type="time"
                      value={preferences.quiet_hours_start || "22:00"}
                      onChange={(e) => setPreferences(prev => ({ ...prev, quiet_hours_start: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">End Time</Label>
                    <input
                      type="time"
                      value={preferences.quiet_hours_end || "07:00"}
                      onChange={(e) => setPreferences(prev => ({ ...prev, quiet_hours_end: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Devices */}
            {subscriptions.length > 0 && (
              <div className="space-y-3">
                <Label className="font-medium flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Connected Devices
                </Label>
                <div className="space-y-2">
                  {subscriptions.map(sub => (
                    <div 
                      key={sub.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">{sub.device_name}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => unsubscribeFromPush(sub.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Critical alerts (wellness, symptom escalation) always come through
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button 
              onClick={savePreferences}
              disabled={saving}
              className="bg-[#4A7C6F] hover:bg-[#3D6B5F]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
